import { useState } from "react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { api, formatApiError } from "../lib/api";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_I18N } from "../i18n/pages";

export default function Contact() {
  const { settings } = useSiteSettings();
  const { lang } = useLang();
  const t = CONTACT_I18N[lang];
  const tr = CONTACT_I18N.tr;
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "", project_type: "", budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, lang });
      setSent(true);
      toast.success(t.successToast);
      setForm({ name: "", company: "", phone: "", email: "", project_type: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title={t.seoTitle} description={t.seoDesc} />
      <PageHero eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />

      <section className="section bg-white">
        <div className="container-x grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-4">
            <InfoCard icon={Phone} title={t.infoPhone} value={settings?.contact_phone || "0543 793 41 01"} href={`tel:${settings?.contact_phone_link || "+905437934101"}`} />
            <InfoCard icon={Mail} title={t.infoEmail} value={settings?.contact_email || "info@dijitalroket.com"} href={`mailto:${settings?.contact_email || "info@dijitalroket.com"}`} />
            <InfoCard icon={MapPin} title={t.infoAddress} value={settings?.contact_address || "Bursa, Türkiye"} />
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 card-elevate p-7 sm:p-10 space-y-5"
          >
            {sent && (
              <div role="status" aria-live="polite" className="flex items-start gap-3 rounded-xl border border-[#10B981]/30 bg-[#10B981]/8 p-4">
                <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5" />
                <div className="text-sm text-[#07111F]">{t.successBox}</div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.labels.name} required>
                <input data-testid="contact-name" required aria-required="true" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder={t.ph.name} />
              </Field>
              <Field label={t.labels.company}>
                <input data-testid="contact-company" autoComplete="organization" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" placeholder={t.ph.company} />
              </Field>
              <Field label={t.labels.phone}>
                <input data-testid="contact-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" placeholder={t.ph.phone} />
              </Field>
              <Field label={t.labels.email} required>
                <input data-testid="contact-email" required aria-required="true" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder={t.ph.email} />
              </Field>
              <Field label={t.labels.projectType}>
                <select data-testid="contact-project-type" value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="input">
                  <option value="">{t.select}</option>
                  {tr.projectTypes.map((value, i) => <option key={value} value={value}>{t.projectTypes[i]}</option>)}
                </select>
              </Field>
              <Field label={t.labels.budget}>
                <select data-testid="contact-budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input">
                  <option value="">{t.select}</option>
                  {tr.budgets.map((value, i) => <option key={value} value={value}>{t.budgets[i]}</option>)}
                </select>
              </Field>
            </div>
            <Field label={t.labels.message} required>
              <textarea
                data-testid="contact-message"
                required
                minLength={10}
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="input resize-none"
                placeholder={t.ph.message}
              />
            </Field>

            <button data-testid="contact-submit" disabled={loading} className="btn-primary w-full sm:w-auto">
              {loading ? t.sending : <>{t.submit} <Send className="h-4 w-4" /></>}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input { width: 100%; border-radius: 0.75rem; border: 1px solid rgb(226 232 240); background: white; padding: 0.75rem 1rem; font-size: 0.9rem; transition: all 0.2s; outline: none; }
        .input:focus { border-color: #2563EB; box-shadow: 0 0 0 4px rgba(37,99,235,0.1); }
      `}</style>
    </>
  );
}

function InfoCard({ icon: Icon, title, value, href }) {
  const inner = (
    <div className="card-elevate p-6 flex items-start gap-4">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB]/10 to-[#22D3EE]/10 border border-[#2563EB]/15">
        <Icon className="h-5 w-5 text-[#2563EB]" />
      </span>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#2563EB]">{title}</div>
        <div className="mt-1 text-[#07111F] font-semibold">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#07111F] mb-1.5">{label}{required && <span className="text-[#F97316]"> *</span>}</span>
      {children}
    </label>
  );
}
